<template>
  <div class="login-overlay" @click.self="close">
    <div class="login-card">

      <div class="login-header">
        <h2>{{ t('editMessage') }}</h2>
        <button class="close-btn" @click="close">&#10005;</button>
      </div>

      <div class="form">
        <textarea
            ref="input"
            v-model="freeText"
            :placeholder="t('writeMessage')"
            rows="4"
            @keydown.enter.ctrl.prevent="submit"
        />

        <!-- Attachment messages keep a tib:// link in the text; only the caption is editable. -->
        <div v-if="attachmentTail" class="attach-hint">{{ t('editKeepsAttachment') }}</div>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <button :disabled="!canSubmit || busy" @click="submit">
          {{ t('save') }}
        </button>
        <button class="secondary-btn" :disabled="busy" @click="close">
          {{ t('cancel') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "../i18n.js"

// Trailing tib:// reference of an attachment message, e.g. "...\n\ntib://domain/<uuid>".
const TIB_TAIL = /(\s*tib:\/\/\S+\s*)$/

export default {
  name: "MessageEditDialog",

  props: {
    service: { type: Object, required: true },
    connectionId: { type: [String, Number], required: true },
    message: { type: Object, required: true }
  },

  emits: ["saved", "close"],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    const raw = this.message?.text || ""
    const m = raw.match(TIB_TAIL)
    // Split off the tib:// tail so only the free text is editable; the tail is re-appended.
    const attachmentTail = m ? m[1].trim() : ""
    const freeText = m ? raw.slice(0, m.index) : raw
    return {
      freeText: freeText.replace(/\s+$/, ""),
      attachmentTail,
      busy: false,
      errorMessage: ""
    }
  },

  computed: {
    canSubmit() {
      // text must not be empty (backend returns 400). For attachment-only messages the
      // tail alone counts as content, so allow an empty caption when a tail exists.
      return this.freeText.trim().length > 0 || this.attachmentTail.length > 0
    }
  },

  mounted() {
    window.addEventListener("keydown", this.onKeydown)
    this.$nextTick(() => this.$refs.input?.focus())
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown)
  },

  methods: {
    onKeydown(e) {
      if (e.key === "Escape") this.close()
    },

    buildText() {
      const free = this.freeText.trim()
      if (this.attachmentTail) {
        return free ? `${free}\n\n${this.attachmentTail}` : this.attachmentTail
      }
      return free
    },

    async submit() {
      if (!this.canSubmit || this.busy) return
      this.busy = true
      this.errorMessage = ""
      try {
        const msgs = await this.service.editMessage(this.connectionId, this.message.id, this.buildText())
        this.$emit("saved", msgs)
      } catch (e) {
        this.errorMessage = e && e.code === "FORBIDDEN"
            ? this.t("editForbidden")
            : this.t("editFailed")
        this.busy = false
      }
    },

    close() {
      if (this.busy) return
      this.$emit("close")
    }
  }
}
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 120;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.login-header h2 { margin: 0; font-size: 18px; }

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
.close-btn:hover { color: #000; }

.login-card {
  width: min(460px, calc(100vw - 32px));
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

textarea {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 16px;
  resize: vertical;
  line-height: 1.4;
}

.attach-hint {
  font-size: 12px;
  color: #6b7280;
  background: #f7f8fa;
  border: 1px solid #e4e8ee;
  border-radius: 6px;
  padding: 8px 10px;
}

button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}
button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.secondary-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}
.secondary-btn:hover:not(:disabled) { background: #e4e8ee; }

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
</style>
